import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "preact/compat";
import { Popup, Toast } from "react-vant";
import { useLocation } from "react-router-dom";
import FloatingBall from "@/components/DraggableFloatingBall";
import { LocalConfig } from "@/config";
import { LanguageContext } from "@/data/LanguageProvider";
import { LocationInfo } from "@/data/LocationInfo";
import { PersistContext } from "@/data/PersistProvider";
import { useEnhancedHistory } from "@/data/RouterProvider";
import { GetDownloadApkUrl } from "@/data/urls";
import {
  ANONYMOUS_CHAT_CACHE_KEY,
  closeAnonymousChatSession,
  getAnonymousChatState,
  sendCustomerChatRequest,
  setAnonymousChatActive,
  setAnonymousChatLastMsgId,
} from "@/utils/CustomerChatAnonymous";
import {
  CUSTOMER_CHAT_BACKGROUND_COOLDOWN_MS,
  CUSTOMER_CHAT_ISSUE_TYPES,
  getBroadcastMessagePreview,
  getCustomerChatBackgroundDelay,
  getCustomerChatState,
  getCustomerChatStateKey,
  isBroadcastMessage,
  isCustomerChatMainInterfacePath,
  markCustomerChatBackgroundRequest,
  markCustomerChatRead,
  normalizeCustomerChatIssueType,
  saveCustomerChatIssueType,
  saveCustomerChatLatestId,
  shouldUseAnonymousCustomerChat,
} from "@/utils/CustomerChatState";
import { SendCMD } from "@/utils/HTTPRequest";
import { consumeCustomerServiceExternalGameReturn, CUSTOMER_SERVICE_OPEN_EVENT } from "@/utils/CustomerService";
import { ChannelRemind } from "@/utils/UrlHelper";
import { FromGameApp, FromStandAlone } from "@/utils/BroswerHelper";
import style from "./style.less";

const CHAT_SEND_CMD = "customerChatSend";
const CHAT_HISTORY_CMD = "customerChatHistory";
const CHAT_HISTORY_BEFORE_CMD = "customerChatHistoryBefore";
const CHAT_IMAGE_UPLOAD_CMD = "customerChatImageUpload";
const POLL_INTERVAL = 5000;
const HISTORY_LIMIT = 20;
const MAX_UNREPLIED_PLAYER_MESSAGES = 10;
const MESSAGE_TYPE_TEXT = 0;
const MESSAGE_TYPE_IMAGE = 1;
const CHAT_CACHE_VERSION = 4;
const CHAT_CACHE_LIMIT = 100;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const BROADCAST_TIP_DURATION = 10 * 1000;
const FLOAT_POSITION_SETTLE_DELAY = 350;
const CustomerServiceFloatIcon = require("@/assets/images/service/icon_kefu.png").default;
const CopyIcon = require("@/assets/icons/copy.png").default;
const PARTNER_GAME_MODE_CLASS = "partner-game-mode";
const SESSION_EXPIRED_MESSAGE = "Your account was logged in elsewhere. Please login again.";
const BEIJING_UTC_OFFSET_MINUTES = 8 * 60;
const HUMAN_SERVICE_BEIJING_START_MINUTES = 15 * 60;
const HUMAN_SERVICE_BEIJING_END_MINUTES = 24 * 60;
const DAY_MINUTES = 24 * 60;
const AUTH_ERROR_KEYWORDS = [
  "token",
  "login",
  "log in",
  "session",
  "expired",
  "expire",
  "invalid",
  "unauthorized",
  "forbidden",
  "elsewhere",
  "another device",
  "other device",
  "kicked",
];

const normalizeDayMinutes = (minutes) => {
  return ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
};

const formatTimeMinutes = (minutes, keepDayEnd = false) => {
  const normalizedMinutes = keepDayEnd && minutes > 0 && minutes % DAY_MINUTES === 0 ? DAY_MINUTES : normalizeDayMinutes(minutes);
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const getTimeZoneUtcOffsetMinutes = (timeZone) => {
  if (!timeZone || typeof Intl === "undefined" || !Intl.DateTimeFormat) {
    return null;
  }

  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      hourCycle: "h23",
    }).formatToParts(now);
    const dateParts = parts.reduce((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
    const localAsUtc = Date.UTC(
      Number(dateParts.year),
      Number(dateParts.month) - 1,
      Number(dateParts.day),
      Number(dateParts.hour),
      Number(dateParts.minute),
      Number(dateParts.second)
    );

    return Math.round((localAsUtc - now.getTime()) / 60000);
  } catch (error) {
    console.warn("Failed to get timezone offset", error);
    return null;
  }
};

const getLastWeekdayOfMonth = (year, monthIndex, weekday) => {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0));
  const diff = (date.getUTCDay() - weekday + 7) % 7;
  date.setUTCDate(date.getUTCDate() - diff);
  return date;
};

const getEgyptUtcOffsetMinutes = (date = new Date()) => {
  const year = date.getUTCFullYear();
  const dstStart = getLastWeekdayOfMonth(year, 3, 5);
  dstStart.setUTCHours(22, 0, 0, 0);
  const dstEnd = getLastWeekdayOfMonth(year, 9, 4);
  dstEnd.setUTCHours(21, 59, 59, 999);
  return date >= dstStart && date <= dstEnd ? 180 : 120;
};

const getLocationUtcOffsetMinutes = (locationInfo) => {
  if (locationInfo.code === "EG") {
    return getEgyptUtcOffsetMinutes();
  }
  const timeZoneOffsetMinutes = getTimeZoneUtcOffsetMinutes(locationInfo.timeZone);
  if (typeof timeZoneOffsetMinutes === "number") {
    return timeZoneOffsetMinutes;
  }
  return typeof locationInfo.utcOffsetMinutes === "number" ? locationInfo.utcOffsetMinutes : 0;
};

const getHumanServiceOnlineTime = (locationInfo) => {
  const utcOffsetMinutes = getLocationUtcOffsetMinutes(locationInfo);
  const startMinutes = HUMAN_SERVICE_BEIJING_START_MINUTES - BEIJING_UTC_OFFSET_MINUTES + utcOffsetMinutes;
  const endMinutes = HUMAN_SERVICE_BEIJING_END_MINUTES - BEIJING_UTC_OFFSET_MINUTES + utcOffsetMinutes;
  const normalizedStartMinutes = normalizeDayMinutes(startMinutes);
  const normalizedEndMinutes = endMinutes > 0 && endMinutes % DAY_MINUTES === 0 ? DAY_MINUTES : normalizeDayMinutes(endMinutes);
  const nextDayText = normalizedEndMinutes < normalizedStartMinutes ? " next day" : "";

  return `${formatTimeMinutes(startMinutes)} - ${formatTimeMinutes(endMinutes, true)}${nextDayText}`;
};

const getCssPixelValue = (propertyName, fallback) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return fallback;
  }

  const value = window.getComputedStyle(document.documentElement).getPropertyValue(propertyName);
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const isPartnerGameMode = () => {
  if (typeof document === "undefined") {
    return false;
  }
  return (
    document.documentElement.classList.contains(PARTNER_GAME_MODE_CLASS) ||
    document.body.classList.contains(PARTNER_GAME_MODE_CLASS)
  );
};

const getCustomerServiceFloatOffset = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      right: "1rem",
      bottom: "6rem",
    };
  }

  const viewportWidth = document.documentElement.clientWidth || window.innerWidth || window.screen.width;
  const partnerGameMode = isPartnerGameMode();
  const rootFontSize = getCssPixelValue("font-size", window.rem || 16);
  let rightGap = 0;

  if (!partnerGameMode) {
    const appElement = document.getElementById("app") || document.getElementById("preact_root");
    const appRect = appElement && appElement.getBoundingClientRect();

    if (appRect && appRect.width) {
      rightGap = Math.max(viewportWidth - appRect.right, 0);
    } else {
      const appWidth = window.appViewportWidth || viewportWidth;
      rightGap = Math.max((viewportWidth - appWidth) / 2, 0);
    }
  }

  return {
    right: `${Math.round(rightGap + rootFontSize)}px`,
    bottom: partnerGameMode ? "1rem" : "6rem",
  };
};

const copyTextToClipboard = async (text) => {
  if (!text || typeof document === "undefined") {
    return false;
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.warn("navigator clipboard failed", error);
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch (error) {
    console.warn("execCommand copy failed", error);
  }
  document.body.removeChild(textarea);
  return copied;
};

const getChatCacheKey = (userInfo, useAnonymous) => {
  if (useAnonymous) {
    return ANONYMOUS_CHAT_CACHE_KEY;
  }
  if (!userInfo || !userInfo.token) {
    return "";
  }
  return `customer_chat_cache_${encodeURIComponent(userInfo.rid || userInfo.token)}`;
};

const normalizeSenderType = (senderType) => {
  if (senderType === null || typeof senderType === "undefined") {
    return -1;
  }

  const senderTypeText = String(senderType).trim();
  if (!senderTypeText) {
    return -1;
  }

  const numericSenderType = Number(senderTypeText);
  if (!Number.isNaN(numericSenderType)) {
    return numericSenderType;
  }

  const typeText = senderTypeText.toLowerCase();
  if (["player", "user", "member", "me", "mine"].includes(typeText)) {
    return 0;
  }
  if (["customer", "service", "admin", "agent", "cs"].includes(typeText)) {
    return 1;
  }
  if (typeText === "system") {
    return 2;
  }
  return -1;
};

const normalizeIdentityValue = (value) => {
  if (value === null || typeof value === "undefined") {
    return "";
  }
  return String(value).trim().toLowerCase();
};

const getCurrentUserIdentitySet = (userInfo) => {
  if (!userInfo) {
    return new Set();
  }

  return new Set(
    [
      userInfo.rid,
      userInfo.id,
      userInfo.uid,
      userInfo.player_id,
      userInfo.playerId,
      userInfo.rolename,
      userInfo.role_name,
      userInfo.role_account,
      userInfo.account,
      userInfo.phone,
      userInfo.mobile,
      userInfo.nick,
      userInfo.name,
    ]
      .map(normalizeIdentityValue)
      .filter(Boolean)
  );
};

const isMyMessage = (message, userInfo) => {
  if (!message) {
    return false;
  }
  const senderType = normalizeSenderType(message.sender_type);
  if (senderType === 0) {
    return true;
  }
  if (senderType === 1 || senderType === 2) {
    return false;
  }

  const userIdentitySet = getCurrentUserIdentitySet(userInfo);
  if (!userIdentitySet.size) {
    return false;
  }

  return [
    message.sender_id,
    message.sender_name,
    message.sender_rid,
    message.sender_uid,
    message.sender_rolename,
    message.sender_role_name,
    message.sender_role_account,
    message.rid,
    message.uid,
    message.player_id,
    message.playerId,
    message.rolename,
    message.role_name,
    message.role_account,
    message.account,
    message.phone,
    message.mobile,
  ]
    .map(normalizeIdentityValue)
    .some((value) => value && userIdentitySet.has(value));
};

const parseJsonIfNeeded = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getPlatform = () => {
  if (FromGameApp) {
    return "2";
  }
  if (FromStandAlone) {
    return "3";
  }
  return "1";
};

const isAuthExpiredText = (text) => {
  if (!text) {
    return false;
  }
  const normalizedText = String(text).toLowerCase();
  return AUTH_ERROR_KEYWORDS.some((keyword) => normalizedText.includes(keyword));
};

const isAuthExpiredResponse = (response) => {
  if (!response) {
    return false;
  }
  if (response.response && (response.response.status === 401 || response.response.status === 403)) {
    return true;
  }

  const responseData = response.response ? response.response.data : response;
  if (!responseData) {
    return false;
  }
  if (responseData.status === 401 || responseData.status === 403) {
    return true;
  }
  return isAuthExpiredText(responseData.code || responseData.errdcode || responseData.error || responseData.errdesc || responseData.message);
};

const getMessageId = (message) => {
  if (typeof message.msg_id !== "undefined") {
    return Number(message.msg_id);
  }
  if (typeof message.id !== "undefined") {
    return Number(message.id);
  }
  return 0;
};

const normalizeTimestamp = (timestamp) => {
  const numericTimestamp = Number(timestamp);
  if (Number.isFinite(numericTimestamp)) {
    if (numericTimestamp <= 0) {
      return 0;
    }
    return numericTimestamp > 100000000000 ? Math.floor(numericTimestamp / 1000) : numericTimestamp;
  }
  const parsedTimestamp = Date.parse(timestamp);
  return Number.isNaN(parsedTimestamp) ? 0 : Math.floor(parsedTimestamp / 1000);
};

const isImageContent = (content) => /\.(png|jpe?g)(?:$|[?#])/i.test(String(content || ""));

const normalizeMessageType = (msgType, content) => {
  if (msgType === null || typeof msgType === "undefined" || String(msgType).trim() === "") {
    return isImageContent(content) ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
  }

  const typeText = String(msgType).trim().toLowerCase();
  const numericType = Number(typeText);
  if (!Number.isNaN(numericType)) {
    return numericType === MESSAGE_TYPE_IMAGE ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
  }
  if (["image", "img", "photo", "picture"].includes(typeText)) {
    return MESSAGE_TYPE_IMAGE;
  }
  return isImageContent(content) ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
};

const isImageMessage = (message) => normalizeMessageType(message && message.msg_type, message && message.content) === MESSAGE_TYPE_IMAGE;

const normalizeMessage = (message) => ({
  msg_id: getMessageId(message),
  sender_type: normalizeSenderType(message.sender_type),
  sender_id: message.sender_id || message.sender_rid || message.rid || message.sender_uid || message.uid || message.player_id || message.playerId || "",
  sender_name: message.sender_name || message.sender_rolename || message.rolename || message.sender_role_name || message.role_name || message.account || "",
  content: message.content || "",
  msg_type: normalizeMessageType(message.msg_type, message.content),
  issue_type: normalizeCustomerChatIssueType(message.issue_type),
  extra: message.extra || "",
  timestamp: normalizeTimestamp(message.timestamp || message.created_at),
  status: "sent",
});

const getHistoryMessages = (data) => {
  const parsedData = parseJsonIfNeeded(data) || {};
  let messages = parseJsonIfNeeded(Array.isArray(parsedData) ? parsedData : parsedData.messages);

  if (!messages) {
    return [];
  }
  if (Array.isArray(messages)) {
    return messages;
  }
  if (typeof messages === "object") {
    const nestedMessages = parseJsonIfNeeded(messages.messages || messages.list || messages.items || messages.data);
    if (Array.isArray(nestedMessages)) {
      return nestedMessages;
    }
    return Object.values(messages).filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (typeof item.msg_id !== "undefined" || typeof item.id !== "undefined" || typeof item.content !== "undefined")
    );
  }
  return [];
};

const isHasMore = (value) => value === true || value === 1 || value === "1" || value === "true";

const getResponseHasMore = (value, messageCount) => {
  if (typeof value === "undefined") {
    return messageCount >= HISTORY_LIMIT;
  }
  return isHasMore(value);
};

const sortMessages = (messages) =>
  [...messages].sort((a, b) => {
    const aMsgId = getMessageId(a);
    const bMsgId = getMessageId(b);
    if (aMsgId && bMsgId && aMsgId !== bMsgId) {
      return aMsgId - bMsgId;
    }
    if (a.timestamp !== b.timestamp) {
      return a.timestamp - b.timestamp;
    }
    return (aMsgId || a.client_time || 0) - (bMsgId || b.client_time || 0);
  });

const mergeMessages = (oldMessages, newMessages) => {
  const merged = [...oldMessages];

  newMessages.forEach((message) => {
    const serverId = getMessageId(message);
    const clientTime = message.client_time ? Number(message.client_time) : 0;
    const existingIndex = merged.findIndex((item) => {
      if (serverId && item.msg_id === serverId) {
        return true;
      }
      if (clientTime && item.client_time === clientTime) {
        return true;
      }
      return false;
    });

    if (existingIndex > -1) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...message,
        msg_id: serverId || merged[existingIndex].msg_id,
        timestamp: normalizeTimestamp(message.timestamp || merged[existingIndex].timestamp),
        status: message.status || "sent",
      };
    } else {
      merged.push(message);
    }
  });

  return sortMessages(merged);
};

const getMaxMessageId = (messages) =>
  messages.reduce((maxId, message) => {
    const msgId = getMessageId(message);
    return msgId > maxId ? msgId : maxId;
  }, 0);

const getMinMessageId = (messages) =>
  messages.reduce((minId, message) => {
    const msgId = getMessageId(message);
    if (!msgId) {
      return minId;
    }
    return minId === 0 || msgId < minId ? msgId : minId;
  }, 0);

const getCacheableMessages = (messages) =>
  sortMessages(
    messages.filter((message) => {
      if (message.status !== "sent" || !getMessageId(message)) {
        return false;
      }
      if (!message.content || String(message.content).indexOf("blob:") === 0) {
        return false;
      }
      return true;
    })
  )
    .slice(-CHAT_CACHE_LIMIT)
    .map((message) => ({
      msg_id: getMessageId(message),
      sender_type: normalizeSenderType(message.sender_type),
      sender_id: message.sender_id || "",
      sender_name: message.sender_name || "",
      content: message.content || "",
      msg_type: normalizeMessageType(message.msg_type, message.content),
      issue_type: normalizeCustomerChatIssueType(message.issue_type),
      extra: message.extra || "",
      timestamp: normalizeTimestamp(message.timestamp),
      status: "sent",
    }));

const readChatCache = (cacheKey) => {
  if (!cacheKey) {
    return [];
  }
  try {
    const cacheText = localStorage.getItem(cacheKey);
    if (!cacheText) {
      return [];
    }
    const cache = JSON.parse(cacheText);
    if (!cache || cache.version !== CHAT_CACHE_VERSION || !Array.isArray(cache.messages)) {
      return [];
    }
    return sortMessages(cache.messages.map(normalizeMessage));
  } catch (error) {
    console.error("read customer chat cache failed", error);
    return [];
  }
};

const writeChatCache = (cacheKey, messages) => {
  if (!cacheKey) {
    return;
  }
  try {
    const cacheMessages = getCacheableMessages(messages);
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        version: CHAT_CACHE_VERSION,
        max_msg_id: getMaxMessageId(cacheMessages),
        updated_at: Date.now(),
        messages: cacheMessages,
      })
    );
  } catch (error) {
    console.error("write customer chat cache failed", error);
  }
};

const countUnrepliedPlayerMessages = (messages, userInfo) => {
  let count = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    const mine = isMyMessage(message, userInfo);
    if (!mine && normalizeSenderType(message.sender_type) !== 2) {
      break;
    }
    if (mine && message.status !== "failed") {
      count++;
    }
  }
  return count;
};

const getMessageDate = (timestamp) => {
  const normalizedTimestamp = normalizeTimestamp(timestamp);
  if (!normalizedTimestamp) {
    return null;
  }

  const date = new Date(normalizedTimestamp * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMessageDate = (timestamp, timeZone) => {
  const date = getMessageDate(timestamp);
  if (!date) {
    return "";
  }

  if (timeZone && typeof Intl !== "undefined" && Intl.DateTimeFormat) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);
      const dateParts = parts.reduce((acc, part) => {
        if (part.type !== "literal") {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});

      if (dateParts.year && dateParts.month && dateParts.day) {
        return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
      }
    } catch (error) {
      console.warn("Failed to format customer chat date", error);
    }
  }

  return `${String(date.getFullYear()).padStart(4, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const formatMessageTime = (timestamp, timeZone) => {
  const date = getMessageDate(timestamp);
  if (!date) {
    return "";
  }

  if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        ...(timeZone ? { timeZone } : {}),
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(date);
    } catch (error) {
      console.warn("Failed to format customer chat time", error);
    }
  }

  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getFileExtension = (file) => {
  const name = (file && file.name ? file.name : "").toLowerCase();
  if (name.endsWith(".png") || file.type === "image/png") {
    return "png";
  }
  if (name.endsWith(".jpg") || name.endsWith(".jpeg") || file.type === "image/jpeg") {
    return "jpg";
  }
  return "";
};

const getImageSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve({});
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });

const getImagePreview = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CustomerServiceWidget = () => {
  const { getPersist, setPersist } = useContext(PersistContext);
  const { langText } = useContext(LanguageContext);
  const history = useEnhancedHistory();
  const location = useLocation();
  const userInfo = getPersist("user_info");
  const userToken = userInfo && userInfo.token;
  const useAnonymous = shouldUseAnonymousCustomerChat(userInfo);
  const currentLocationInfo = LocationInfo();
  const humanServiceOnlineTime = `${getHumanServiceOnlineTime(currentLocationInfo)} (${currentLocationInfo.name} Time)`;
  const chatCacheKey = useMemo(() => getChatCacheKey(userInfo, useAnonymous), [userInfo, useAnonymous]);
  const chatStateKey = useMemo(() => getCustomerChatStateKey(userInfo, useAnonymous), [userInfo, useAnonymous]);
  const chatIdentityRef = useRef(null);
  if (
    !chatIdentityRef.current ||
    chatIdentityRef.current.stateKey !== chatStateKey ||
    chatIdentityRef.current.token !== userToken
  ) {
    // Invalidate callbacks during render, before identity-reset effects run.
    chatIdentityRef.current = { stateKey: chatStateKey, token: userToken };
  }
  const chatIdentity = chatIdentityRef.current;
  const isCurrentChatIdentity = useCallback((identity) => chatIdentityRef.current === identity, []);
  const [issueTypeSelection, setIssueTypeSelection] = useState(() => ({
    stateKey: chatStateKey,
    value: getCustomerChatState(chatStateKey).issueType,
  }));
  const selectedIssueType = issueTypeSelection.stateKey === chatStateKey
    ? issueTypeSelection.value
    : getCustomerChatState(chatStateKey).issueType;
  useEffect(() => {
    setIssueTypeSelection({ stateKey: chatStateKey, value: getCustomerChatState(chatStateKey).issueType });
  }, [chatStateKey]);
  const selectIssueType = (value) => {
    const state = saveCustomerChatIssueType(chatStateKey, value);
    setIssueTypeSelection({ stateKey: chatStateKey, value: state.issueType });
  };
  const [visible, setVisible] = useState(false);
  const [gameMode, setGameMode] = useState(false);
  const isMainInterface = isCustomerChatMainInterfacePath(location.pathname) && !gameMode;
  const [hasUnread, setHasUnread] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState(null);
  const [floatingBallSide, setFloatingBallSide] = useState("right");
  const [targetMessageId, setTargetMessageId] = useState(0);
  const [portalContainer, setPortalContainer] = useState(null);
  const [floatBallOffset, setFloatBallOffset] = useState(getCustomerServiceFloatOffset);
  const [floatBallResetKey, setFloatBallResetKey] = useState(0);
  const floatBallOffsetRef = useRef(floatBallOffset);
  const [downloadApkUrl, setDownloadApkUrl] = useState("");
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const pollTimerRef = useRef(null);
  const homeRequestTimerRef = useRef(null);
  const lastBackgroundRequestAtRef = useRef(0);
  const backgroundRequestInFlightRef = useRef(false);
  const visibleRef = useRef(false);
  const broadcastTipTimerRef = useRef(null);
  const floatingBallSideTimerRef = useRef(null);
  const serviceFloatBallRef = useRef(null);
  const loadingHistoryRef = useRef(false);
  const loadingHistoryBeforeRef = useRef(false);
  const fileInputRef = useRef(null);
  const chatBodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const maxMsgIdRef = useRef(0);
  const minMsgIdRef = useRef(0);
  const hasMoreBeforeRef = useRef(true);
  const skipNextScrollToBottomRef = useRef(false);
  const skipNextCacheWriteRef = useRef(false);
  const chatServiceUnavailableRef = useRef(false);
  const automaticRequestsPausedRef = useRef(false);
  const authRedirectingRef = useRef(false);

  const unrepliedCount = useMemo(() => countUnrepliedPlayerMessages(messages, userInfo), [messages, userInfo]);
  const sendLimited = unrepliedCount >= MAX_UNREPLIED_PLAYER_MESSAGES;
  const sendDisabled = sending || uploading || sendLimited;
  const inputDisabled = sending || uploading;

  const updateFloatBallOffset = useCallback((forceReset = false) => {
    const nextOffset = getCustomerServiceFloatOffset();
    const prevOffset = floatBallOffsetRef.current;
    const hasChanged = prevOffset.right !== nextOffset.right || prevOffset.bottom !== nextOffset.bottom;

    if (hasChanged) {
      floatBallOffsetRef.current = nextOffset;
      setFloatBallOffset(nextOffset);
    }
    if (hasChanged || forceReset) {
      setFloatBallResetKey((key) => key + 1);
    }
  }, []);

  const syncGameMode = useCallback(() => {
    setGameMode(isPartnerGameMode());
    updateFloatBallOffset();
  }, [updateFloatBallOffset]);

  const requestCustomerChat = useCallback(
    (cmd, payload) =>
      sendCustomerChatRequest({
        cmd,
        payload,
        playerToken: userToken,
        useAnonymous,
        sendCmd: SendCMD,
      }),
    [useAnonymous, userToken]
  );

  const handleChatRequestFailure = useCallback((response) => {
    if (response && response.stopAutomaticRetry) {
      automaticRequestsPausedRef.current = true;
    }
  }, []);

  const markAnonymousChatActive = useCallback(() => {
    if (!useAnonymous) {
      return;
    }
    setAnonymousChatActive(true);
  }, [useAnonymous]);

  const handleAnonymousSessionStatus = useCallback(
    (sessionStatus) => {
      if (!useAnonymous || String(sessionStatus || "").toLowerCase() !== "closed") {
        return;
      }
      closeAnonymousChatSession();
    },
    [useAnonymous]
  );

  useEffect(() => {
    if (!useAnonymous && getAnonymousChatState().active) {
      setAnonymousChatActive(false);
    }
  }, [useAnonymous]);

  const persistLatestMessageId = useCallback(
    (lastMsgId, markUnread = false) => {
      const result = saveCustomerChatLatestId(chatStateKey, lastMsgId, markUnread);
      setHasUnread(result.unread);

      if (useAnonymous && result.lastMsgId > getAnonymousChatState().lastMsgId) {
        setAnonymousChatLastMsgId(result.lastMsgId);
      }
      return result;
    },
    [chatStateKey, useAnonymous]
  );

  const clearUnread = useCallback(() => {
    markCustomerChatRead(chatStateKey);
    setHasUnread(false);
  }, [chatStateKey]);

  const updateFloatingBallSide = useCallback(() => {
    if (!serviceFloatBallRef.current || typeof window === "undefined") {
      return;
    }
    const rect = serviceFloatBallRef.current.getBoundingClientRect();
    const nextSide = rect.left + rect.width / 2 > window.innerWidth / 2 ? "right" : "left";
    setFloatingBallSide((currentSide) => (currentSide === nextSide ? currentSide : nextSide));
  }, []);

  const handleFloatingBallTouchEnd = useCallback(() => {
    if (floatingBallSideTimerRef.current) {
      clearTimeout(floatingBallSideTimerRef.current);
    }
    floatingBallSideTimerRef.current = setTimeout(() => {
      floatingBallSideTimerRef.current = null;
      updateFloatingBallSide();
    }, FLOAT_POSITION_SETTLE_DELAY);
  }, [updateFloatingBallSide]);

  useEffect(() => {
    if (broadcastTipTimerRef.current) {
      clearTimeout(broadcastTipTimerRef.current);
      broadcastTipTimerRef.current = null;
    }
    if (!broadcastMessage) {
      return undefined;
    }

    updateFloatingBallSide();
    broadcastTipTimerRef.current = setTimeout(() => {
      broadcastTipTimerRef.current = null;
      setBroadcastMessage(null);
    }, BROADCAST_TIP_DURATION);

    return () => {
      if (broadcastTipTimerRef.current) {
        clearTimeout(broadcastTipTimerRef.current);
        broadcastTipTimerRef.current = null;
      }
    };
  }, [broadcastMessage, updateFloatingBallSide]);

  useEffect(() => {
    window.addEventListener("resize", updateFloatingBallSide);
    return () => {
      window.removeEventListener("resize", updateFloatingBallSide);
      if (floatingBallSideTimerRef.current) {
        clearTimeout(floatingBallSideTimerRef.current);
        floatingBallSideTimerRef.current = null;
      }
    };
  }, [updateFloatingBallSide]);

  const redirectToLogin = useCallback(
    (message = SESSION_EXPIRED_MESSAGE) => {
      if (authRedirectingRef.current) {
        return;
      }

      authRedirectingRef.current = true;
      const backUrl = location.pathname + location.search;
      setPersist("login_back_url", backUrl);
      setPersist("user_info", null);
      setPersist("gatesvr", null);
      setVisible(false);
      chatServiceUnavailableRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      Toast.info(message);
      history.replace(
        ChannelRemind(`/login?${getPersist("last_phone") ? "" : "reg=1&"}back_to=${encodeURIComponent(backUrl)}`)
      );
    },
    [getPersist, history, location.pathname, location.search, setPersist]
  );

  const verifyCurrentSession = useCallback(async () => {
    if (!isCurrentChatIdentity(chatIdentity)) {
      return false;
    }
    const currentUserInfo = getPersist("user_info");
    if (!currentUserInfo || !currentUserInfo.token) {
      redirectToLogin();
      return false;
    }

    try {
      const res = await SendCMD("getMe", {
        plat: getPlatform(),
        token: currentUserInfo.token,
      });
      if (!isCurrentChatIdentity(chatIdentity)) {
        return false;
      }
      if (!res || !res.status) {
        redirectToLogin();
        return false;
      }
      if (res.player_info) {
        setPersist("user_info", res.player_info);
        setPersist("gatesvr", res.gatesvr);
      }
      return true;
    } catch (error) {
      if (!isCurrentChatIdentity(chatIdentity)) {
        return false;
      }
      console.error("customer service session verify failed", error);
      return true;
    }
  }, [chatIdentity, getPersist, isCurrentChatIdentity, redirectToLogin, setPersist]);

  const handleSessionExpired = useCallback(
    async (source) => {
      if (!isCurrentChatIdentity(chatIdentity)) {
        return true;
      }
      if (useAnonymous) {
        return false;
      }
      if (authRedirectingRef.current) {
        return true;
      }
      if (isAuthExpiredResponse(source)) {
        redirectToLogin();
        return true;
      }
      if (source && source.ok === false) {
        return !(await verifyCurrentSession());
      }
      return false;
    },
    [chatIdentity, isCurrentChatIdentity, redirectToLogin, useAnonymous, verifyCurrentSession]
  );

  useEffect(() => {
    if (userToken) {
      authRedirectingRef.current = false;
    }
    skipNextCacheWriteRef.current = true;
    setInputValue("");
    setLoadingHistory(false);
    setSending(false);
    setUploading(false);
    loadingHistoryRef.current = false;
    loadingHistoryBeforeRef.current = false;
    backgroundRequestInFlightRef.current = false;
    skipNextScrollToBottomRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!chatCacheKey || !chatStateKey) {
      setMessages([]);
      maxMsgIdRef.current = 0;
      minMsgIdRef.current = 0;
      hasMoreBeforeRef.current = true;
      setHistoryLoaded(false);
      setHasUnread(false);
      setBroadcastMessage(null);
      setVisible(false);
      visibleRef.current = false;
      return;
    }

    const cachedMessages = readChatCache(chatCacheKey);
    let storedChatState = getCustomerChatState(chatStateKey);
    const anonymousLastMsgId = useAnonymous ? getAnonymousChatState().lastMsgId : 0;
    const existingLastMsgId = Math.max(getMaxMessageId(cachedMessages), anonymousLastMsgId);
    if (existingLastMsgId > storedChatState.lastMsgId) {
      storedChatState = saveCustomerChatLatestId(chatStateKey, existingLastMsgId, false);
    }
    setMessages(cachedMessages);
    maxMsgIdRef.current = Math.max(existingLastMsgId, storedChatState.lastMsgId);
    minMsgIdRef.current = getMinMessageId(cachedMessages);
    hasMoreBeforeRef.current = true;
    setHistoryLoaded(cachedMessages.length > 0);
    setHasUnread(storedChatState.unread);
    setBroadcastMessage(null);
    lastBackgroundRequestAtRef.current = storedChatState.lastBackgroundRequestAt;
  }, [chatCacheKey, chatStateKey, useAnonymous, userToken]);

  useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  useEffect(() => {
    let mounted = true;

    Promise.resolve(GetDownloadApkUrl("")).then((url) => {
      if (mounted) {
        setDownloadApkUrl(url);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    syncGameMode();

    if (typeof MutationObserver === "undefined") {
      window.addEventListener("resize", syncGameMode);
      return () => {
        window.removeEventListener("resize", syncGameMode);
      };
    }

    const observer = new MutationObserver(syncGameMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => {
      observer.disconnect();
    };
  }, [location.pathname, location.search, syncGameMode]);

  useEffect(() => {
    const scheduledTimers = [];
    const updateFloatOffset = () => {
      updateFloatBallOffset(true);
      scheduledTimers.forEach((timer) => window.clearTimeout(timer));
      scheduledTimers.length = 0;
      scheduledTimers.push(window.setTimeout(updateFloatBallOffset, 80));
      scheduledTimers.push(window.setTimeout(updateFloatBallOffset, 320));
    };

    updateFloatOffset();
    window.addEventListener("resize", updateFloatOffset);
    window.addEventListener("orientationchange", updateFloatOffset);
    document.addEventListener("fullscreenchange", updateFloatOffset);
    document.addEventListener("webkitfullscreenchange", updateFloatOffset);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateFloatOffset);
    }

    return () => {
      window.removeEventListener("resize", updateFloatOffset);
      window.removeEventListener("orientationchange", updateFloatOffset);
      document.removeEventListener("fullscreenchange", updateFloatOffset);
      document.removeEventListener("webkitfullscreenchange", updateFloatOffset);
      scheduledTimers.forEach((timer) => window.clearTimeout(timer));
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateFloatOffset);
      }
    };
  }, [gameMode, updateFloatBallOffset]);

  useEffect(() => {
    const restoreFromExternalGame = () => {
      syncGameMode();
      if (document.visibilityState === "hidden") {
        return;
      }
      if (!consumeCustomerServiceExternalGameReturn()) {
        return;
      }

      Toast.clear(true);
      chatServiceUnavailableRef.current = false;
      setVisible(false);
    };

    restoreFromExternalGame();
    window.addEventListener("pageshow", restoreFromExternalGame);
    window.addEventListener("focus", restoreFromExternalGame);
    document.addEventListener("visibilitychange", restoreFromExternalGame);

    return () => {
      window.removeEventListener("pageshow", restoreFromExternalGame);
      window.removeEventListener("focus", restoreFromExternalGame);
      document.removeEventListener("visibilitychange", restoreFromExternalGame);
    };
  }, [syncGameMode]);

  useEffect(() => {
    if (!chatCacheKey) {
      return;
    }
    if (skipNextCacheWriteRef.current) {
      skipNextCacheWriteRef.current = false;
      return;
    }
    writeChatCache(chatCacheKey, messages);
  }, [chatCacheKey, messages]);

  const mergeAndStoreMessages = useCallback((incomingMessages, requestIdentity) => {
    if (!isCurrentChatIdentity(requestIdentity) || !incomingMessages || incomingMessages.length === 0) {
      return;
    }

    setMessages((prevMessages) => {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return prevMessages;
      }
      const merged = mergeMessages(prevMessages, incomingMessages);
      maxMsgIdRef.current = Math.max(maxMsgIdRef.current, getMaxMessageId(merged));
      minMsgIdRef.current = getMinMessageId(merged);
      return merged;
    });
  }, [isCurrentChatIdentity]);

  const fetchHistory = useCallback(
    async (sinceMsgId, loopMore) => {
      const requestIdentity = chatIdentity;
      if (
        !isCurrentChatIdentity(requestIdentity) ||
        loadingHistoryRef.current ||
        chatServiceUnavailableRef.current ||
        automaticRequestsPausedRef.current
      ) {
        return;
      }
      loadingHistoryRef.current = true;
      setLoadingHistory(true);

      try {
        const response = await requestCustomerChat(CHAT_HISTORY_CMD, {
          since_msg_id: sinceMsgId,
          limit: HISTORY_LIMIT,
        });
        if (!isCurrentChatIdentity(requestIdentity)) {
          return;
        }

        if (!response.ok) {
          handleChatRequestFailure(response);
          if ((await handleSessionExpired(response)) || !isCurrentChatIdentity(requestIdentity)) {
            return;
          }
          Toast.fail(response.error || "Get history failed");
          return;
        }

        const res = response.data;
        const normalizedMessages = getHistoryMessages(res).map(normalizeMessage);
        mergeAndStoreMessages(normalizedMessages, requestIdentity);

        const responseLastMsgId = Number(res.last_msg_id) || 0;
        const newMaxMsgId = Math.max(
          maxMsgIdRef.current,
          responseLastMsgId,
          getMaxMessageId(normalizedMessages)
        );
        maxMsgIdRef.current = newMaxMsgId;
        // A reply arriving after the chat was closed still needs an unread badge.
        persistLatestMessageId(newMaxMsgId, !visibleRef.current);
        const incomingMinMsgId = getMinMessageId(normalizedMessages);
        if (incomingMinMsgId) {
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, incomingMinMsgId) : incomingMinMsgId;
        }
        if (!sinceMsgId) {
          hasMoreBeforeRef.current = getResponseHasMore(res.has_more, normalizedMessages.length);
        }
        setHistoryLoaded(true);
        handleAnonymousSessionStatus(res.session_status);

        if (loopMore && isHasMore(res.has_more) && normalizedMessages.length > 0) {
          setTimeout(() => {
            if (isCurrentChatIdentity(requestIdentity)) {
              fetchHistory(newMaxMsgId, true);
            }
          }, 0);
        }
      } catch (error) {
        if (!isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        console.error("customer chat history failed", error);
        if ((await handleSessionExpired(error)) || !isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        if (error && error.response && error.response.status === 404) {
          chatServiceUnavailableRef.current = true;
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          Toast.fail("Customer service API not found");
          return;
        }
        Toast.fail("Get history failed");
      } finally {
        if (isCurrentChatIdentity(requestIdentity)) {
          loadingHistoryRef.current = false;
          setLoadingHistory(false);
        }
      }
    },
    [
      chatIdentity,
      handleAnonymousSessionStatus,
      handleChatRequestFailure,
      handleSessionExpired,
      isCurrentChatIdentity,
      mergeAndStoreMessages,
      persistLatestMessageId,
      requestCustomerChat,
    ]
  );

  const fetchBackgroundHistory = useCallback(async () => {
    const requestIdentity = chatIdentity;
    if (
      !isCurrentChatIdentity(requestIdentity) ||
      !chatStateKey ||
      backgroundRequestInFlightRef.current ||
      chatServiceUnavailableRef.current ||
      automaticRequestsPausedRef.current
    ) {
      return;
    }

    backgroundRequestInFlightRef.current = true;
    lastBackgroundRequestAtRef.current = Date.now();
    markCustomerChatBackgroundRequest(chatStateKey, lastBackgroundRequestAtRef.current);
    const savedState = getCustomerChatState(chatStateKey);
    try {
      const response = await requestCustomerChat(CHAT_HISTORY_CMD, {
        since_msg_id: savedState.lastMsgId,
        limit: HISTORY_LIMIT,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }

      if (!response.ok) {
        handleChatRequestFailure(response);
        if ((await handleSessionExpired(response)) || !isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        return;
      }

      const res = response.data;
      const normalizedMessages = getHistoryMessages(res).map(normalizeMessage);
      mergeAndStoreMessages(normalizedMessages, requestIdentity);

      const latestMsgId = Math.max(
        savedState.lastMsgId,
        Number(res.last_msg_id) || 0,
        getMaxMessageId(normalizedMessages)
      );
      maxMsgIdRef.current = Math.max(maxMsgIdRef.current, latestMsgId);
      const incomingMinMsgId = getMinMessageId(normalizedMessages);
      if (incomingMinMsgId) {
        minMsgIdRef.current = minMsgIdRef.current
          ? Math.min(minMsgIdRef.current, incomingMinMsgId)
          : incomingMinMsgId;
      }

      const shouldNotify = !visibleRef.current;
      const savedResult = persistLatestMessageId(latestMsgId, shouldNotify);
      handleAnonymousSessionStatus(res.session_status);

      if (shouldNotify && savedResult.isNew) {
        const broadcastMessages = normalizedMessages.filter(
          (message) => getMessageId(message) > savedResult.previousLastMsgId && isBroadcastMessage(message)
        );
        if (broadcastMessages.length) {
          setBroadcastMessage(sortMessages(broadcastMessages).slice(-1)[0]);
        }
      }
    } catch (error) {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      console.error("customer chat background history failed", error);
      if ((await handleSessionExpired(error)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      if (error && error.response && error.response.status === 404) {
        chatServiceUnavailableRef.current = true;
      }
    } finally {
      if (isCurrentChatIdentity(requestIdentity)) {
        backgroundRequestInFlightRef.current = false;
      }
    }
  }, [
    chatIdentity,
    chatStateKey,
    handleAnonymousSessionStatus,
    handleChatRequestFailure,
    handleSessionExpired,
    isCurrentChatIdentity,
    mergeAndStoreMessages,
    persistLatestMessageId,
    requestCustomerChat,
  ]);

  const fetchHistoryBefore = useCallback(async () => {
    const requestIdentity = chatIdentity;
    const beforeMsgId = minMsgIdRef.current;
    if (
      !isCurrentChatIdentity(requestIdentity) ||
      !beforeMsgId ||
      !hasMoreBeforeRef.current ||
      loadingHistoryBeforeRef.current ||
      chatServiceUnavailableRef.current ||
      automaticRequestsPausedRef.current
    ) {
      return;
    }

    const chatBody = chatBodyRef.current;
    const previousScrollHeight = chatBody ? chatBody.scrollHeight : 0;
    const previousScrollTop = chatBody ? chatBody.scrollTop : 0;
    loadingHistoryBeforeRef.current = true;

    try {
      const response = await requestCustomerChat(CHAT_HISTORY_BEFORE_CMD, {
        before_msg_id: beforeMsgId,
        limit: HISTORY_LIMIT,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }

      if (!response.ok) {
        handleChatRequestFailure(response);
        if ((await handleSessionExpired(response)) || !isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        Toast.fail(response.error || "Get history failed");
        return;
      }

      const res = response.data;
      const normalizedMessages = getHistoryMessages(res).map(normalizeMessage);
      if (!normalizedMessages.length) {
        hasMoreBeforeRef.current = false;
        return;
      }

      skipNextScrollToBottomRef.current = true;
      mergeAndStoreMessages(normalizedMessages, requestIdentity);
      persistLatestMessageId(
        Math.max(Number(res.last_msg_id) || 0, getMaxMessageId(normalizedMessages)),
        false
      );

      const incomingMinMsgId = getMinMessageId(normalizedMessages);
      if (incomingMinMsgId) {
        minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, incomingMinMsgId) : incomingMinMsgId;
      }
      hasMoreBeforeRef.current = getResponseHasMore(res.has_more, normalizedMessages.length);

      requestAnimationFrame(() => {
        if (!isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        const currentChatBody = chatBodyRef.current;
        if (currentChatBody && previousScrollHeight) {
          currentChatBody.scrollTop = currentChatBody.scrollHeight - previousScrollHeight + previousScrollTop;
        }
      });
    } catch (error) {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      console.error("customer chat history before failed", error);
      if ((await handleSessionExpired(error)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      Toast.fail("Get history failed");
    } finally {
      if (isCurrentChatIdentity(requestIdentity)) {
        loadingHistoryBeforeRef.current = false;
      }
    }
  }, [
    chatIdentity,
    handleChatRequestFailure,
    handleSessionExpired,
    isCurrentChatIdentity,
    mergeAndStoreMessages,
    persistLatestMessageId,
    requestCustomerChat,
  ]);

  const handleChatScroll = useCallback(
    (event) => {
      if (event.currentTarget.scrollTop <= 8) {
        fetchHistoryBefore();
      }
    },
    [fetchHistoryBefore]
  );

  const openPopup = useCallback((messageId = 0) => {
    chatServiceUnavailableRef.current = false;
    automaticRequestsPausedRef.current = false;
    lastBackgroundRequestAtRef.current = Date.now();
    markCustomerChatBackgroundRequest(chatStateKey, lastBackgroundRequestAtRef.current);
    if (homeRequestTimerRef.current) {
      clearTimeout(homeRequestTimerRef.current);
      homeRequestTimerRef.current = null;
    }
    clearUnread();
    setBroadcastMessage(null);
    setTargetMessageId(Number(messageId) || 0);
    visibleRef.current = true;
    setVisible(true);
  }, [chatStateKey, clearUnread]);

  const closeToFloat = () => {
    visibleRef.current = false;
    setVisible(false);
    setTargetMessageId(0);
  };

  const copyDownloadApkUrl = async () => {
    if (!downloadApkUrl) {
      Toast.fail("Download link not ready");
      return;
    }

    const copied = await copyTextToClipboard(downloadApkUrl);
    if (copied) {
      Toast.success("Download link copied");
      return;
    }
    Toast.fail("Copy failed");
  };

  const appendLocalMessage = (message) => {
    setMessages((prevMessages) =>
      isCurrentChatIdentity(chatIdentity) ? sortMessages([...prevMessages, message]) : prevMessages
    );
  };

  const updateLocalMessage = (clientTime, update) => {
    setMessages((prevMessages) => {
      if (!isCurrentChatIdentity(chatIdentity)) {
        return prevMessages;
      }
      return sortMessages(
        prevMessages.map((message) => {
          if (message.client_time === clientTime) {
            return { ...message, ...update };
          }
          return message;
        })
      );
    });
  };

  const sendChatMessage = async ({ content, msgType, extra, previewContent }) => {
    const requestIdentity = chatIdentity;
    if (!isCurrentChatIdentity(requestIdentity) || !content) {
      return;
    }
    if (sendLimited) {
      Toast.info("Please wait for customer service reply");
      return;
    }

    const issueType = normalizeCustomerChatIssueType(selectedIssueType);
    const clientTime = Date.now();
    appendLocalMessage({
      local_id: `local-${clientTime}`,
      client_time: clientTime,
      sender_type: 0,
      sender_name: "Me",
      content: previewContent || content,
      server_content: content,
      msg_type: msgType,
      issue_type: issueType,
      extra: extra || "",
      timestamp: Math.floor(clientTime / 1000),
      status: "pending",
    });

    setSending(true);
    try {
      const response = await requestCustomerChat(CHAT_SEND_CMD, {
        content,
        msg_type: msgType,
        issue_type: issueType,
        extra: extra || "",
        client_time: clientTime,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }

      if (response.ok) {
        automaticRequestsPausedRef.current = false;
        const res = response.data;
        updateLocalMessage(clientTime, {
          msg_id: res.msg_id ? Number(res.msg_id) : 0,
          content,
          timestamp: normalizeTimestamp(res.timestamp) || Math.floor(clientTime / 1000),
          status: "sent",
        });
        if (res.msg_id) {
          const msgId = Number(res.msg_id);
          maxMsgIdRef.current = Math.max(maxMsgIdRef.current, msgId);
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, msgId) : msgId;
        }
        persistLatestMessageId(Math.max(Number(res.last_msg_id) || 0, Number(res.msg_id) || 0), false);
        markAnonymousChatActive();
        return;
      }

      updateLocalMessage(clientTime, { status: "failed" });
      handleChatRequestFailure(response);
      if ((await handleSessionExpired(response)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      Toast.fail(response.error || "Send failed");
    } catch (error) {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      console.error("customer chat send failed", error);
      updateLocalMessage(clientTime, { status: "failed" });
      if ((await handleSessionExpired(error)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      Toast.fail("Send failed");
    } finally {
      if (isCurrentChatIdentity(requestIdentity)) {
        setSending(false);
      }
    }
  };

  const sendTextMessage = () => {
    const content = inputValue.trim();
    if (!content) {
      return;
    }
    setInputValue("");
    sendChatMessage({
      content,
      msgType: MESSAGE_TYPE_TEXT,
    });
  };

  const uploadImageFile = async (file) => {
    const requestIdentity = chatIdentity;
    if (!isCurrentChatIdentity(requestIdentity)) {
      return;
    }
    // Keep this message's selection while previewing and uploading asynchronously.
    const issueType = normalizeCustomerChatIssueType(selectedIssueType);
    const fileExtension = getFileExtension(file);
    console.error("fileExtension:", fileExtension)
    if (!fileExtension) {
      Toast.info("Only jpg/png images are supported");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      Toast.info("Image must be less than 5MB");
      return;
    }
    if (sendLimited) {
      Toast.info("Please wait for customer service reply");
      return;
    }

    let previewUrl = "";
    let imageSize = {};
    try {
      [previewUrl, imageSize] = await Promise.all([getImagePreview(file), getImageSize(file)]);
    } catch (error) {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      console.error("customer chat image preview failed", error);
      Toast.fail("Image preview failed");
      return;
    }
    if (!isCurrentChatIdentity(requestIdentity)) {
      return;
    }
    const extra = JSON.stringify(imageSize);
    const clientTime = Date.now();

    appendLocalMessage({
      local_id: `local-${clientTime}`,
      client_time: clientTime,
      sender_type: 0,
      sender_name: "Me",
      content: previewUrl,
      msg_type: MESSAGE_TYPE_IMAGE,
      issue_type: issueType,
      extra,
      timestamp: Math.floor(clientTime / 1000),
      status: "uploading",
    });
    setUploading(true);
    try {
      const uploadResponse = await requestCustomerChat(CHAT_IMAGE_UPLOAD_CMD, {
        file_extension: fileExtension,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }

      const uploadRes = uploadResponse.data;
      if (!uploadResponse.ok || !uploadRes.upload_url || !uploadRes.image_url) {
        updateLocalMessage(clientTime, { status: "failed" });
        handleChatRequestFailure(uploadResponse);
        if ((await handleSessionExpired(uploadResponse)) || !isCurrentChatIdentity(requestIdentity)) {
          return;
        }
        Toast.fail(uploadResponse.error || "Image upload failed");
        return;
      }
      automaticRequestsPausedRef.current = false;
      const putRes = await fetch(uploadRes.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || `image/${fileExtension}`,
        },
        body: file,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      if (!putRes.ok) {
        updateLocalMessage(clientTime, { status: "failed" });
        Toast.fail("Image upload failed");
        return;
      }

      updateLocalMessage(clientTime, {
        content: uploadRes.image_url,
        msg_type: MESSAGE_TYPE_IMAGE,
        status: "pending",
      });

      const sendResponse = await requestCustomerChat(CHAT_SEND_CMD, {
        content: uploadRes.image_url,
        msg_type: MESSAGE_TYPE_IMAGE,
        extra,
        issue_type: issueType,
        client_time: clientTime,
      });
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }

      if (sendResponse.ok) {
        automaticRequestsPausedRef.current = false;
        const sendRes = sendResponse.data;
        updateLocalMessage(clientTime, {
          msg_id: sendRes.msg_id ? Number(sendRes.msg_id) : 0,
          content: uploadRes.image_url,
          msg_type: MESSAGE_TYPE_IMAGE,
          timestamp: normalizeTimestamp(sendRes.timestamp) || Math.floor(clientTime / 1000),
          status: "sent",
        });
        if (sendRes.msg_id) {
          const msgId = Number(sendRes.msg_id);
          maxMsgIdRef.current = Math.max(maxMsgIdRef.current, msgId);
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, msgId) : msgId;
        }
        persistLatestMessageId(
          Math.max(Number(sendRes.last_msg_id) || 0, Number(sendRes.msg_id) || 0),
          false
        );
        markAnonymousChatActive();
        return;
      }

      updateLocalMessage(clientTime, { status: "failed" });
      handleChatRequestFailure(sendResponse);
      if ((await handleSessionExpired(sendResponse)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      Toast.fail(sendResponse.error || "Send failed");
    } catch (error) {
      if (!isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      console.error("customer chat image failed", error);
      updateLocalMessage(clientTime, { status: "failed" });
      if ((await handleSessionExpired(error)) || !isCurrentChatIdentity(requestIdentity)) {
        return;
      }
      Toast.fail("Image upload failed");
    } finally {
      if (isCurrentChatIdentity(requestIdentity)) {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  useEffect(() => {
    const handleOpen = () => {
      openPopup();
    };
    window.addEventListener(CUSTOMER_SERVICE_OPEN_EVENT, handleOpen);
    return () => {
      window.removeEventListener(CUSTOMER_SERVICE_OPEN_EVENT, handleOpen);
    };
  }, [openPopup]);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (homeRequestTimerRef.current) {
      clearTimeout(homeRequestTimerRef.current);
      homeRequestTimerRef.current = null;
    }
    if (!isMainInterface || visible || !chatStateKey) {
      return undefined;
    }

    let stopped = false;
    const scheduleBackgroundRequest = (delay) => {
      if (stopped) {
        return;
      }
      homeRequestTimerRef.current = setTimeout(() => {
        homeRequestTimerRef.current = null;
        if (stopped) {
          return;
        }
        fetchBackgroundHistory();
        scheduleBackgroundRequest(CUSTOMER_CHAT_BACKGROUND_COOLDOWN_MS);
      }, delay);
    };

    const requestDelay = getCustomerChatBackgroundDelay(lastBackgroundRequestAtRef.current);
    scheduleBackgroundRequest(requestDelay);

    return () => {
      stopped = true;
      if (homeRequestTimerRef.current) {
        clearTimeout(homeRequestTimerRef.current);
        homeRequestTimerRef.current = null;
      }
    };
  }, [chatStateKey, fetchBackgroundHistory, isMainInterface, visible]);

  useEffect(() => {
    if (!visible) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }

    fetchHistory(historyLoaded ? maxMsgIdRef.current : 0, false);
    pollTimerRef.current = setInterval(() => {
      fetchHistory(maxMsgIdRef.current, false);
    }, POLL_INTERVAL);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchHistory, historyLoaded, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (skipNextScrollToBottomRef.current) {
      skipNextScrollToBottomRef.current = false;
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView(false);
    }
  }, [messages.length, visible]);

  useEffect(() => {
    if (!visible || !targetMessageId || !chatBodyRef.current) {
      return;
    }
    const targetElement = chatBodyRef.current.querySelector(
      `[data-message-id="${targetMessageId}"]`
    );
    if (targetElement && typeof targetElement.scrollIntoView === "function") {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [messages.length, targetMessageId, visible]);

  const customerServiceContent = (
    <>
      <Popup
        visible={visible}
        className={`${style.customer_service_popup} ${gameMode ? style.customer_service_popup_game : ""}`}
        closeOnClickOverlay={false}
        onClose={closeToFloat}
      >
        <div class={style.service_window}>
          <button class={style.close_btn} onClick={closeToFloat}>
            x
          </button>
          <div class={style.chat_shell}>
            <div class={style.chat_profile}>
              <img src={require("@/assets/images/home/kftb_1.png").default} />
              <div class={style.chat_profile_content}>
                <div class={style.chat_profile_name}>{LocalConfig.gameName} Customer Service</div>
                <div class={style.chat_profile_time}>
                  {langText("customer_service_human_time", { param1: humanServiceOnlineTime })}
                </div>
                <div class={style.chat_profile_desc}>App must be updated to the latest package to upload images.</div>
                {downloadApkUrl ? (
                  <div class={style.chat_profile_download_row}>
                    <a class={style.chat_profile_download} href={downloadApkUrl} target="_blank" rel="noreferrer">
                      Download latest APK
                    </a>
                    <button class={style.chat_profile_copy} type="button" title="Copy download link" onClick={copyDownloadApkUrl}>
                      <img src={CopyIcon} alt="" />
                      <span>Copy</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div class={style.chat_body} ref={chatBodyRef} onScroll={handleChatScroll}>
              {loadingHistory && !historyLoaded && messages.length === 0 ? (
                <div class={style.chat_loading}>
                  <div class={style.loading_dots}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              ) : null}
              {messages.map((message) => {
                const isMine = isMyMessage(message, userInfo);
                const messageDate = formatMessageDate(message.timestamp, currentLocationInfo.timeZone);
                const messageTime = formatMessageTime(message.timestamp, currentLocationInfo.timeZone);
                const messageKey = message.msg_id || message.local_id || message.client_time;
                return (
                  <div
                    key={messageKey}
                    data-message-id={getMessageId(message) || undefined}
                    data-issue-type={normalizeCustomerChatIssueType(message.issue_type)}
                    class={`${style.message_row} ${isMine ? style.message_row_mine : ""} ${
                      getMessageId(message) === targetMessageId ? style.message_highlight : ""
                    }`}
                  >
                    {!isMine ? (
                      <div class={style.bot_name_row}>
                        <img src={require("@/assets/images/home/kftb_1.png").default} />
                        <span>{message.sender_name || (message.sender_type === 2 ? "System" : "Bot")}</span>
                      </div>
                    ) : null}
                    <div class={`${style.message_bubble} ${isMine ? style.message_bubble_mine : ""}`}>
                      <div class={style.message_issue_type}>
                        {langText(`customer_chat_issue_type_${normalizeCustomerChatIssueType(message.issue_type)}`)}
                      </div>
                      {isImageMessage(message) ? (
                        <img class={style.message_image} src={message.content} />
                      ) : (
                        <p>{message.content}</p>
                      )}
                      <div class={style.message_meta}>
                        {messageDate && messageTime ? (
                          <span class={style.message_timestamp}>{`${messageDate} ${messageTime}`}</span>
                        ) : null}
                        {message.status === "pending" || message.status === "uploading" ? <span>Sending</span> : null}
                        {message.status === "failed" ? <span>Failed</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div class={style.chat_input_area}>
              <div class={style.issue_type_label}>{langText("customer_chat_issue_type")}</div>
              <div class={style.issue_type_options} role="radiogroup" aria-label={langText("customer_chat_issue_type")}>
                {CUSTOMER_CHAT_ISSUE_TYPES.map((issueType) => (
                  <button
                    key={issueType}
                    type="button"
                    role="radio"
                    aria-checked={selectedIssueType === issueType}
                    tabIndex={selectedIssueType === issueType ? 0 : -1}
                    data-issue-type={issueType}
                    class={`${style.issue_type_option} ${selectedIssueType === issueType ? style.issue_type_selected : ""}`}
                    onClick={() => selectIssueType(issueType)}
                    onKeyDown={(event) => {
                      const index = CUSTOMER_CHAT_ISSUE_TYPES.indexOf(issueType);
                      const lastIndex = CUSTOMER_CHAT_ISSUE_TYPES.length - 1;
                      const nextIndex = {
                        ArrowRight: (index + 1) % CUSTOMER_CHAT_ISSUE_TYPES.length,
                        ArrowDown: (index + 1) % CUSTOMER_CHAT_ISSUE_TYPES.length,
                        ArrowLeft: (index + lastIndex) % CUSTOMER_CHAT_ISSUE_TYPES.length,
                        ArrowUp: (index + lastIndex) % CUSTOMER_CHAT_ISSUE_TYPES.length,
                        Home: 0,
                        End: lastIndex,
                      }[event.key];
                      if (typeof nextIndex === "undefined") {
                        return;
                      }
                      event.preventDefault();
                      selectIssueType(CUSTOMER_CHAT_ISSUE_TYPES[nextIndex]);
                      event.currentTarget.parentNode.children[nextIndex].focus();
                    }}
                  >
                    {langText(`customer_chat_issue_type_${issueType}`)}
                  </button>
                ))}
              </div>
              <input
                value={inputValue}
                placeholder={sendLimited ? "Please wait for customer service reply" : "Input message"}
                disabled={inputDisabled}
                onInput={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    sendTextMessage();
                  }
                }}
              />
              {sendLimited ? <div class={style.send_limit_tip}>Please wait for customer service reply</div> : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = "";
                  if (file) {
                    uploadImageFile(file);
                  }
                }}
              />
              <div class={style.chat_input_tools}>
                <button
                  class={style.tool_button}
                  aria-label="Send image"
                  title="Send image"
                  disabled={sendDisabled}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  <span class={style.image_tool_icon} />
                </button>
                <button
                  class={`${style.send_button} ${inputValue.trim() && !sendDisabled ? style.send_active : ""}`}
                  disabled={!inputValue.trim() || sendDisabled}
                  onClick={sendTextMessage}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      {!visible ? (
        <div class={`${style.float_ball_container} customer-service-float-ball ${gameMode ? style.float_ball_container_game : ""}`}>
          <FloatingBall
            key={`customer-service-${floatBallResetKey}-${floatBallOffset.right}-${floatBallOffset.bottom}`}
            offset={floatBallOffset}
            adsorb={{
              distance: 15,
            }}
            onClick={() => openPopup()}
          >
            <div
              ref={serviceFloatBallRef}
              class={style.service_float_ball}
              role="button"
              aria-label={langText("home_btn_contact")}
              onTouchEnd={handleFloatingBallTouchEnd}
              onPointerUp={handleFloatingBallTouchEnd}
            >
              <img class={style.service_float_icon} src={CustomerServiceFloatIcon} alt="" />
              <span class={style.service_float_label}>{langText("home_btn_contact")}</span>
              {hasUnread ? <span class={style.unread_badge} /> : null}
              {broadcastMessage ? (
                <div
                  class={`${style.broadcast_tip} ${
                    floatingBallSide === "right"
                      ? style.broadcast_tip_to_left
                      : style.broadcast_tip_to_right
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPopup(getMessageId(broadcastMessage));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.stopPropagation();
                      openPopup(getMessageId(broadcastMessage));
                    }
                  }}
                >
                  <span class={style.broadcast_tip_text}>
                    <strong>Kefu:</strong>{" "}
                    {isImageMessage(broadcastMessage)
                      ? "You have a new image message"
                      : getBroadcastMessagePreview(broadcastMessage)}
                  </span>
                </div>
              ) : null}
            </div>
          </FloatingBall>
        </div>
      ) : null}
    </>
  );

  return portalContainer ? createPortal(customerServiceContent, portalContainer) : customerServiceContent;
};

export default CustomerServiceWidget;
